import { useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  MultiFactorResolver,
  getMultiFactorResolver,
  PhoneMultiFactorInfo,
  MultiFactorError,
} from 'firebase/auth'
import { RecaptchaVerifier } from 'firebase/auth'
import { createContext, useEffect, useRef, useState } from 'react'
import {
  fbauth,
  inviteUser,
  loginWithMicrosoft as signInWithMicrosoftProvider,
  registerUser,
  auth as userAuth,
  updateUser,
  initRecaptchaVerifier,
  enrollMFA,
  completeMFAEnrollment,
  initMfaSignInVerification,
  verifyMFA,
  unenrollMFA,
  getEnrolledMFAFactors,
  sendFirebaseEmailVerification,
  checkEmailVerified,
  applyEmailVerificationCode,
  changePassword,
} from '../../api/auth/auth'
import { setChatbotMode } from '../../api/workforce/workforce'
import handleToast from '../../components/HandleToast'
import { setCachedChatbotMode } from '../../lib/performance/queryOptions'
import { LoginCredentials, RegisterUserType, User, UserContextType } from '../../types/auth/auth'

// Extended user type for the data returned by /user/self/ (includes fields beyond the base User interface)
interface AuthUser extends User {
  role?: string
  mfa_required?: boolean
  mfa_phone?: string | null
  language?: string
}

const syncInitialChatbotMode = async (
  authUser: AuthUser,
  queryClient: ReturnType<typeof useQueryClient>,
  idToken?: string,
) => {
  if (!authUser.is_admin) return

  try {
    await setChatbotMode('admin', idToken)
    setCachedChatbotMode(queryClient, 'admin')
  } catch (error) {
    console.warn('Failed to sync initial chatbot mode after login', error)
  }
}

export const AuthContext = createContext<UserContextType>({} as UserContextType)

export function useAuth() {
  const { t } = useTranslation()
  const [loginLoading, setLoginLoading] = useState(false)
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [mfaPhoneNumber, setMfaPhoneNumber] = useState<string | null>(null)
  const [mfaEmail, setMfaEmail] = useState<string | null>(null)
  const [mfaRequiredForUser, setMfaRequiredForUser] = useState<boolean>(false)
  const [mfaLanguage, setMfaLanguage] = useState<string | undefined>(undefined)
  const [mfaEnrollmentVerificationId, setMfaEnrollmentVerificationId] = useState<string | null>(null)
  const [mfaSignInVerificationId, setMfaSignInVerificationId] = useState<string | null>(null)
  const skipNextMfaSyncFromUserRef = useRef(false)
  const lastKnownAuthUidRef = useRef<string | null>(null)
  const justLoggedInRef = useRef(false)
  const queryClient = useQueryClient()
  const toast = useToast()
  const auth = fbauth

  // Only fetch /user/self/ when Firebase has provided auth credentials (avoid requests before login)
  const [hasAuthCredentials, setHasAuthCredentials] = useState<boolean | null>(null)

  const resetAuthFlowState = () => {
    setLoginLoading(false)
    setMfaResolver(null)
    setRequiresMFA(false)
    setMfaPhoneNumber(null)
    setMfaEmail(null)
    setMfaRequiredForUser(false)
    setMfaLanguage(undefined)
    setMfaEnrollmentVerificationId(null)
    setMfaSignInVerificationId(null)
    skipNextMfaSyncFromUserRef.current = false
  }

  const clearStoredAuthContext = (clearSessionStorage = false) => {
    localStorage.removeItem('is_admin')
    localStorage.removeItem('role')
    localStorage.removeItem('backend_role')

    try {
      if (clearSessionStorage) {
        sessionStorage.clear()
      } else {
        sessionStorage.removeItem('mfa_verified_session')
        sessionStorage.removeItem('hasShownSessionError')
      }
    } catch {
      // Ignore sessionStorage access errors.
    }
  }

  const clearSessionScopedClientState = async (clearSessionStorage = false) => {
    resetAuthFlowState()
    clearStoredAuthContext(clearSessionStorage)
    await queryClient.cancelQueries()
    queryClient.clear()
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const currentUid = firebaseUser?.uid || null
      const previousUid = lastKnownAuthUidRef.current

      if (previousUid && previousUid !== currentUid) {
        await clearSessionScopedClientState(true)
      } else if (previousUid && !currentUid) {
        await clearSessionScopedClientState(true)
      }

      lastKnownAuthUidRef.current = currentUid
      setHasAuthCredentials(!!firebaseUser)
    })
    // Fallback: if Firebase doesn't report within 2.5s (e.g. offline/error), show login so user isn't stuck
    const fallback = window.setTimeout(() => {
      setHasAuthCredentials((prev) => (prev === null ? false : prev))
    }, 2500)
    return () => {
      unsubscribe()
      window.clearTimeout(fallback)
    }
  }, [auth])

  // MFA: same as login — required only until completed in this session. Once verified, not asked again until logout.
  const mfaVerifiedThisSession =
    typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('mfa_verified_session')

  const {
    data: user,
    isError: isAuthError,
    error: authQueryError,
    isPending: isAuthPending,
    isSuccess,
  } = useQuery({
    queryKey: ['authUser'],
    queryFn: userAuth,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled:
      hasAuthCredentials === true &&
      !requiresMFA &&
      (!mfaRequiredForUser || mfaVerifiedThisSession),
  })

  const isAuthenticated =
    isSuccess &&
    !!user &&
    !requiresMFA &&
    (!mfaRequiredForUser || mfaVerifiedThisSession)

  const isVerified = user ? !!user.is_verified : null

  // When the authUser query fails (e.g. on page reload with valid Firebase token but backend error),
  // show an error and sign out so the user isn't stuck on a blank screen.
  // Use sessionStorage to prevent the toast from re-appearing on page navigation/remount.
  useEffect(() => {
    const hasShownSessionErrorKey = 'hasShownSessionError'

    if (isAuthError && hasAuthCredentials) {
      let hasShownError = false
      try {
        hasShownError = sessionStorage.getItem(hasShownSessionErrorKey) === 'true'
      } catch {
        hasShownError = false
      }

      if (!hasShownError) {
        const detail = (authQueryError as Error)?.message || t('auth.errors.userFetchFailed')
        handleToast(toast, false, t('auth.errors.sessionError'), detail, 5000)

        // Mark that we've shown this error so it won't repeat on remount/navigation.
        try {
          sessionStorage.setItem(hasShownSessionErrorKey, 'true')
        } catch {
          // Ignore sessionStorage access errors.
        }

        // Sign out of Firebase to avoid a half-authenticated loop
        signOut(auth).catch(() => { /* ignore sign-out errors */ })
        setHasAuthCredentials(false)
        void clearSessionScopedClientState(false)
      }
    }

    // Clear dedupe flag after auth error is resolved so future real errors can be shown once.
    if (!isAuthError) {
      try {
        sessionStorage.removeItem(hasShownSessionErrorKey)
      } catch {
        // Ignore sessionStorage access errors.
      }
    }
  }, [isAuthError, authQueryError, hasAuthCredentials, auth, t, toast])

  // Sync MFA state from backend user so reload on MFA page doesn't bypass (mfaRequiredForUser is otherwise only set during login).
  // Skip syncing mfaRequiredForUser when we just completed MFA sign-in verification, or we'd overwrite the "verified" state and block access.
  useEffect(() => {
    if (skipNextMfaSyncFromUserRef.current) {
      skipNextMfaSyncFromUserRef.current = false
      if (user && user.mfa_phone !== undefined) {
        setMfaPhoneNumber(user.mfa_phone || null)
      }
      return
    }
    if (user && typeof user.mfa_required === 'boolean') {
      setMfaRequiredForUser(!!user.mfa_required)
    }
    if (user && user.mfa_phone !== undefined) {
      setMfaPhoneNumber(user.mfa_phone || null)
    }
  }, [user?.mfa_required, user?.mfa_phone, user])

  // Sync role to localStorage on first load OR when the backend role actually changes
  // (e.g. promotion/demotion). Do NOT overwrite on every render — the TopBar role-switch
  // feature sets role to 'user' to simulate "user mode" and overwriting would undo that.
  const userRole = (user as AuthUser | undefined)?.role
  useEffect(() => {
    if (!user) return
    const authUser = user as AuthUser
    const backendRole = authUser.is_admin ? (authUser.role || 'user') : 'user'
    const prevBackendRole = localStorage.getItem('backend_role')
    const storedRole = localStorage.getItem('role')
    // Write role on first load (no stored role) or when the backend role genuinely changed
    if (!storedRole || (prevBackendRole !== null && prevBackendRole !== backendRole)) {
      localStorage.setItem('is_admin', authUser.is_admin ? 'true' : 'false')
      localStorage.setItem('role', backendRole)
    }
    localStorage.setItem('backend_role', backendRole)
  }, [user?.is_admin, userRole, user])

  // Run one-time post-login setup when the reactive authUser query succeeds after a manual login.
  useEffect(() => {
    if (!justLoggedInRef.current || !user) return
    justLoggedInRef.current = false
    const authUser = user as AuthUser
    void syncInitialChatbotMode(authUser, queryClient)
  }, [user, queryClient])

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      setLoginLoading(true)
      try {
        await signInWithEmailAndPassword(auth, credentials.username.toLowerCase(), credentials.password)
      } catch (error: unknown) {
        const firebaseError = error as MultiFactorError
        if (firebaseError.code === 'auth/multi-factor-auth-required') {
          // MFA is required - user is already enrolled and needs to verify

          // In Firebase v9, use getMultiFactorResolver to extract the resolver
          const resolver = getMultiFactorResolver(auth, firebaseError)

          setMfaResolver(resolver)
          setRequiresMFA(true)
          setMfaEmail(credentials.username.toLowerCase())

          // Extract phone number from the MFA hint if available
          if (resolver.hints?.[0]) {
            const hint = resolver.hints[0] as PhoneMultiFactorInfo
            const maskedPhone = hint.phoneNumber
            setMfaPhoneNumber(maskedPhone)
          }

          setLoginLoading(false)
          throw new Error('MFA_REQUIRED')
        } else if (auth.currentUser) {
          return
        } else {
          throw error
        }
      }
    },
    onSuccess: () => {
      setRequiresMFA(false)
      setMfaResolver(null)
      justLoggedInRef.current = true
      setLoginLoading(false)
    },
    onError: (error: Error) => {
      if (error.message === 'MFA_REQUIRED') {
        handleToast(toast, true, t('auth.mfa.toastMfaRequired'), t('auth.mfa.toastMfaRequiredDesc'), 2000)
      } else {
        handleToast(toast, false, 'Login failed', 'Your login credentials are invalid', 2000)
        setLoginLoading(false)
      }
    },
  })

  const loginWithMicrosoftMutation = useMutation({
    mutationFn: async () => {
      setLoginLoading(true)
      try {
        return await signInWithMicrosoftProvider()
      } catch (error: unknown) {
        const firebaseError = error as MultiFactorError & { customData?: { email?: string } }
        if (firebaseError.code === 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(auth, firebaseError)

          setMfaResolver(resolver)
          setRequiresMFA(true)
          setMfaEmail(firebaseError.customData?.email?.toLowerCase() || null)

          if (resolver.hints?.[0]) {
            const hint = resolver.hints[0] as PhoneMultiFactorInfo
            setMfaPhoneNumber(hint.phoneNumber)
          }

          setLoginLoading(false)
          throw new Error('MFA_REQUIRED')
        }

        throw error
      }
    },
    onSuccess: () => {
      setRequiresMFA(false)
      setMfaResolver(null)
      justLoggedInRef.current = true
      setLoginLoading(false)
    },
    onError: (error: Error) => {
      if (error.message === 'MFA_REQUIRED') {
        handleToast(toast, true, t('auth.mfa.toastMfaRequired'), t('auth.mfa.toastMfaRequiredDesc'), 2000)
      } else {
        const errorWithCode = error as Error & { code?: string }
        const isExistingPasswordAccount =
          errorWithCode.code === 'auth/account-exists-with-different-credential'
        handleToast(
          toast,
          false,
          'Microsoft sign-in failed',
          isExistingPasswordAccount
            ? 'This email already uses password sign-in. Sign in with your password first, then link Microsoft from Account Settings.'
            : error.message || 'Unable to continue with Microsoft.',
          10000,
        )
        setLoginLoading(false)
      }
    },
  })

  const registerMutation = useMutation({
    mutationFn: async (userData: RegisterUserType | FormData) => {
      setLoginLoading(true)

      // Extract email and password for Firebase auth
      let email: string
      let password: string

      if (userData instanceof FormData) {
        email = (userData.get('email') as string).toLowerCase()
        password = userData.get('password') as string
        // Update FormData with lowercase email
        userData.set('email', email)
      } else {
        email = userData.email.toLowerCase()
        password = userData.password
        userData = {
          ...userData,
          email: email
        }
      }

      await registerUser(userData)
      await signInWithEmailAndPassword(auth, email, password)
    },
    onSuccess: async () => {
      // Refetch and wait for the auth user query to complete
      await queryClient.refetchQueries({ queryKey: ['authUser'] })
      setLoginLoading(false)
      handleToast(toast, true, 'Registration successful', 'success')
    },
    onError: (error: Error) => {
      setLoginLoading(false)
      handleToast(toast, false, 'Registration failed', error.message)
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { password: string }) => {
      setLoginLoading(true)
      await changePassword(data)
    },
    onSuccess: () => {
      setLoginLoading(false)
      handleToast(toast, true, 'Password updated successfully', 'success')
    },
    onError: (error: Error) => {
      setLoginLoading(false)
      handleToast(toast, false, 'Password update failed', error.message)
    },
  })

  const changeUserMutation = useMutation({
    mutationFn: async (userData: string | boolean) => {
      await updateUser(userData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authUser'] })
      handleToast(toast, true, 'User profile picture updated successfully', 'success')
    },
    onError: (error: Error) => {
      setLoginLoading(false)
      handleToast(toast, false, 'User update failed', error.message)
    },
  })

  const inviteUserMutation = useMutation({
    mutationFn: async ({ userData, invite_code }: { userData: RegisterUserType | FormData; invite_code: string }) => {
      setLoginLoading(true)

      // Extract email and password for Firebase auth
      let email: string
      let password: string

      if (userData instanceof FormData) {
        email = (userData.get('email') as string).toLowerCase()
        password = userData.get('password') as string
        // Update FormData with lowercase email
        userData.set('email', email)
      } else {
        email = userData.email.toLowerCase()
        password = userData.password
        userData = {
          ...userData,
          email: email
        }
      }

      await inviteUser(userData, invite_code)
      await signInWithEmailAndPassword(auth, email, password)
    },
    onSuccess: async () => {
      // Refetch and wait for the auth user query to complete (same as registerMutation)
      await queryClient.refetchQueries({ queryKey: ['authUser'] })
      setLoginLoading(false)
      handleToast(toast, true, 'Registration successful', 'success')
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      setLoginLoading(false)
      handleToast(toast, false, 'Registration failed', error.response?.data?.detail || error.message)
    },
  })

  const forgotPassword = async (email: string) => {
    setLoginLoading(true)
    await sendPasswordResetEmail(auth, email)
    handleToast(toast, true, 'If an account exists, a password reset email has been sent', 'success')
    setLoginLoading(false)
  }

  const loginWithUsernameAndPassword = async (username: string, password: string) => {
    return loginMutation.mutateAsync({ username, password })
  }

  const logout = async () => {
    await clearSessionScopedClientState(true)
    lastKnownAuthUidRef.current = null
    await signOut(auth)
    setHasAuthCredentials(false)
  }

  const register = async (userData: RegisterUserType | FormData) => {
    return registerMutation.mutateAsync(userData)
  }

  const loginWithMicrosoft = async () => {
    return loginWithMicrosoftMutation.mutateAsync()
  }

  const inviteUserWithCode = async (userData: RegisterUserType | FormData, invite_code: string) => {
    return inviteUserMutation.mutateAsync({ userData, invite_code })
  }

  const updatePassword = async (password: string) => {
    return changePasswordMutation.mutateAsync({ password })
  }

  const updateUserCredentials = async (data: string | boolean) => {
    return changeUserMutation.mutateAsync(data)
  }

  const verifyMFACode = async (verificationCode: string) => {
    setLoginLoading(true)

    try {
      // Check if we're completing enrollment or verifying sign-in
      if (mfaEnrollmentVerificationId) {
        // Complete MFA enrollment
        await completeMFAEnrollment(mfaEnrollmentVerificationId, verificationCode, 'Primary Phone')
        setMfaEnrollmentVerificationId(null)
        setMfaRequiredForUser(false)
        setMfaLanguage(undefined)
        skipNextMfaSyncFromUserRef.current = true
        queryClient.invalidateQueries({ queryKey: ['authUser'] })
        handleToast(toast, true, t('auth.mfa.toastEnrollmentSuccess'), t('auth.mfa.toastEnrollmentDesc'), 2000)
      } else if (mfaResolver && mfaSignInVerificationId) {
        // Verify MFA during sign-in
        await verifyMFA(mfaResolver, mfaSignInVerificationId, verificationCode)
        setRequiresMFA(false)
        setMfaResolver(null)
        setMfaSignInVerificationId(null)
        setMfaRequiredForUser(false)
        setMfaPhoneNumber(null)
        setMfaEmail(null)
        setMfaLanguage(undefined)
        // Same as login: mark session as MFA-complete so we don't ask again until logout
        try {
          sessionStorage.setItem('mfa_verified_session', '1')
        } catch { /* ignore sessionStorage errors */ }
        // Skip syncing mfaRequiredForUser from next user fetch so we don't overwrite the "verified" state (backend still has mfa_required: true)
        skipNextMfaSyncFromUserRef.current = true

        // Check if backend still requires MFA. If an admin disabled MFA for this user
        // but Firebase still had factors enrolled, auto-unenroll now that sign-in is complete.
        try {
          const authUser = await queryClient.fetchQuery({ queryKey: ['authUser'], queryFn: userAuth }) as AuthUser
          if (authUser && !authUser.mfa_required) {
            const factors = getEnrolledMFAFactors()
            for (const factor of factors) {
              try {
                await unenrollMFA(factor.uid)
              } catch { /* ignore individual unenroll failures */ }
            }
          }
          if (authUser) {
            localStorage.setItem('is_admin', authUser?.is_admin ? 'true' : 'false')
            localStorage.setItem('role', authUser?.role || 'user')
            await syncInitialChatbotMode(authUser, queryClient)
          }
        } catch { /* ignore fetch errors during MFA cleanup */ }

        queryClient.invalidateQueries({ queryKey: ['authUser'] })
        handleToast(toast, true, t('auth.mfa.toastLoginSuccess'), '', 2000)
      } else {
        console.error('[MFA] No MFA context available:', {
          mfaEnrollmentVerificationId,
          mfaSignInVerificationId,
          mfaResolver,
        })
        throw new Error('No MFA context available')
      }
    } catch (error: unknown) {
      const errorMsg = (error as Error).message || t('auth.mfa.invalidVerificationCode')
      console.error('[MFA] Verification failed:', error)
      handleToast(toast, false, t('auth.mfa.toastVerificationFailed'), errorMsg, 2000)
      throw error
    } finally {
      setLoginLoading(false)
    }
  }

  const initMfaSignInVerificationFlow = async () => {
    if (!mfaResolver) {
      console.error('[MFA] No MFA resolver available, mfaResolver is:', mfaResolver)
      throw new Error('No MFA resolver available')
    }

    try {
      const verificationId = await initMfaSignInVerification(mfaResolver)
      setMfaSignInVerificationId(verificationId)
      return verificationId
    } catch (error: unknown) {
      console.error('[MFA] initMfaSignInVerificationFlow error:', error)
      handleToast(toast, false, t('auth.mfa.toastSendCodeFailed'), (error as Error).message, 2000)
      throw error
    }
  }

  const enrollUserInMFA = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => {
    try {
      const verificationId = await enrollMFA(phoneNumber, recaptchaVerifier)
      // Store the verification ID for completing enrollment
      setMfaEnrollmentVerificationId(verificationId)
      // Store the phone number we enrolled with for future reference
      setMfaPhoneNumber(phoneNumber)
      return verificationId
    } catch (error: unknown) {
      handleToast(toast, false, t('auth.mfa.toastEnrollmentFailed'), (error as Error).message, 2000)
      throw error
    }
  }

  const checkMFAEnrollment = async () => {
    const factors = await getEnrolledMFAFactors()
    return factors.length > 0
  }

  return {
    error: {
      mutation: loginMutation.isError || registerMutation.isError || changePasswordMutation.isError,
      query: isAuthError,
    },
    loading: {
      mutation: loginLoading || registerMutation.isPending || changePasswordMutation.isPending,
      // Show loading only while Firebase is initializing (null) or during the FIRST user fetch (no cached data yet).
      // Do NOT include isFetching — background refetches must not unmount the router.
      query:
        hasAuthCredentials === null ||
        (hasAuthCredentials === true &&
          !requiresMFA &&
          (!mfaRequiredForUser || mfaVerifiedThisSession) &&
          isAuthPending),
    },
    user,
    isAuthenticated,
    isVerified,
    requiresMFA,
    mfaResolver,
    mfaPhoneNumber,
    mfaRequiredForUser,
    mfaVerifiedThisSession,
    mfaLanguage,
    mfaEmail,
    setMfaRequiredForUser,
    changeUserMutation,
    loginWithUsernameAndPassword,
    loginWithMicrosoft,
    logout,
    forgotPassword,
    register,
    inviteUserWithCode,
    updateUserCredentials,
    updatePassword,
    verifyMFACode,
    enrollUserInMFA,
    initMfaSignInVerificationFlow,
    getEnrolledMFAFactors,
    checkMFAEnrollment,
    initRecaptchaVerifier,
    unenrollMFA,
    sendEmailVerification: sendFirebaseEmailVerification,
    checkEmailVerified,
    applyEmailVerificationCode,
  }
}
